import { useCallback, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * List state that survives Back: one query-string key per piece of state.
 *
 * Search, filter chips and sort order used to live in `useState` on the list
 * screens, so opening a record and pressing Back remounted the list with its
 * defaults and the reader lost the results they were working through. Putting
 * them in the URL makes the history entry itself remember them.
 *
 * - Writes REPLACE the current entry. Typing a search must not push one history
 *   entry per keystroke, or Back would walk back through the letters.
 * - The key is DROPPED when the value equals its default, so an untouched list
 *   keeps a clean URL and an inbound link's own params are not rewritten.
 * - Other params (`jobId`, …) and `location.state` are carried through.
 *
 * THE VALUE RETURNED IS A LOCAL MIRROR, NOT THE URL. React Router applies a
 * navigation as a transition, so the URL-derived value lags the keystroke. A
 * controlled input bound straight to it re-rendered with the old text between
 * keys, and typing "vermilion" on /jobs left `q=n` — each key replaced the one
 * before. The mirror updates in the same event; the URL catches up behind it.
 * It re-syncs from the URL only when the URL changes to something this hook did
 * not write (Back, Forward, a link), and one of our own writes landing late is
 * recognised and skipped rather than dragging the text backwards.
 *
 * Writes build on the last search THIS hook family wrote, not on the rendered
 * location, for the same reason: two setters in one handler (sort field, then
 * direction), or a chip click and then a keystroke before the router commits,
 * would otherwise each start from the old URL and the second would drop the
 * first. `lastWrite` is used only while the URL is still where we left it —
 * either not yet caught up (same entry, still `from`) or caught up (`to`).
 * Keying the first case to the entry matters: a later visit to the same path
 * also starts at `from`, and must not inherit a search typed on the last one.
 * Anything else means
 * the URL moved for another reason, and the rendered location wins.
 */
let lastWrite: { path: string; key: string; from: string; to: string } | null = null;

export function useUrlState<T extends string>(
  key: string,
  defaultValue: T,
): [T, (next: T | ((prev: T) => T)) => void] {
  const location = useLocation();
  const navigate = useNavigate();

  const raw = new URLSearchParams(location.search).get(key);
  const urlValue = (raw ?? defaultValue) as T;

  const [value, setLocal] = useState<T>(urlValue);
  const [seenUrlValue, setSeenUrlValue] = useState<T>(urlValue);
  const written = useRef<T[]>([]);
  if (urlValue !== seenUrlValue) {
    setSeenUrlValue(urlValue);
    const i = written.current.indexOf(urlValue);
    if (i >= 0) {
      written.current.splice(0, i + 1);
    } else {
      written.current = [];
      setLocal(urlValue);
    }
  }

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      const ours =
        lastWrite !== null &&
        lastWrite.path === location.pathname &&
        ((location.key === lastWrite.key && location.search === lastWrite.from) ||
          location.search === lastWrite.to);
      const base = ours && lastWrite ? lastWrite.to : location.search;
      const params = new URLSearchParams(base);
      const prev = (params.get(key) ?? defaultValue) as T;
      const resolved = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
      setLocal(resolved);
      if (resolved !== prev) written.current.push(resolved);
      if (resolved === defaultValue) params.delete(key);
      else params.set(key, resolved);
      const search = params.toString();
      const searchWithMark = search ? `?${search}` : '';
      if (searchWithMark === base) return;
      const pending = ours && lastWrite !== null && location.search === lastWrite.from;
      lastWrite = {
        path: location.pathname,
        key: location.key,
        from: pending && lastWrite ? lastWrite.from : location.search,
        to: searchWithMark,
      };
      navigate(
        { pathname: location.pathname, search: searchWithMark, hash: location.hash },
        { replace: true, state: location.state },
      );
    },
    [key, defaultValue, location.search, location.pathname, location.hash, location.state, location.key, navigate],
  );

  return [value, setValue];
}

/**
 * For tests only. Every MemoryRouter's first entry has the key `'default'`, so
 * a write left over from one test looks like a pending write to the next. In
 * the app the first entry is the page load, and a reload resets the module.
 */
export function __resetUrlStateForTests() {
  lastWrite = null;
}
